'use client';

import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import './XmlEditorPopup.css';
import sheetEditorEntity from '../entities/SheetEditor/SheetEditor';
import { dpAssert } from '@/utils/helpers';
import FormHeader from './ui/popover-header';

import { IconButton } from './ui/button';
import { PiCheckLight, PiDotsSixVerticalBold, PiXLight } from 'react-icons/pi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Icons } from './ui/icons';
import { Separator } from './ui/separator';
import { useDraggable } from '@dnd-kit/core';
import { Label } from './ui/label';
import { NumberInput } from './NumberInput';
import samplesSelectorModel from './Samples/SamplesSelector/SamplesSelectorModel';
import { observable } from 'mobx';
import { useTranslation } from 'react-i18next';

const SheetEditorPopup = observer(() => {
  const { x, y } = sheetEditorEntity.popupPosition;

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'popup',
  });

  const X_OFFSET_STEP = 10;

  const style: React.CSSProperties = {
    width: 254,
    position: 'absolute',
    zIndex: 50,
    left: x,
    top: y,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  };

  const applyChanges = () => {
    sheetEditorEntity.applyChangesForCurrentNote();
  };
  const cancelChanges = () => {
    sheetEditorEntity.cancelChangesForCurrentNote();
  };
  const [hand, setHand] = useState(0);

  if (sheetEditorEntity.selectedNoteId && sheetEditorEntity.selectedNote) {
    if (hand != sheetEditorEntity.selectedNote.hand) {
      setHand(sheetEditorEntity.selectedNote.hand);
    }
  }

  useEffect(() => {
    if (sheetEditorEntity.selectedNote) {
      dpAssert(sheetEditorEntity.selectedNote?.midi);

      const currentSample = samplesSelectorModel.selectedSample;
      const existing = currentSample?.notesCustomAttributes?.find(
        (n) =>
          n.measureIndex === sheetEditorEntity.selectedNote?.measureIndex &&
          n.indexInXmlParent ===
            sheetEditorEntity.selectedNote?.midi?.indexInXmlParent
      );

      sheetEditorEntity.notesCustomAttributesEditor.currNoteCustomAttributes =
        observable({
          indexInXmlParent:
            sheetEditorEntity.selectedNote.midi.indexInXmlParent,
          measureIndex: sheetEditorEntity.selectedNote.measureIndex,
          hand: sheetEditorEntity.selectedNote.hand,
          xOffset: existing?.xOffset ?? 0,
        });
    }
    // eslint-disable-next-line
  }, [sheetEditorEntity.selectedNoteId]);

  const handleHandChange = (event: any) => {
    const handValue = parseInt(event.target.value);
    setHand(handValue);
    if (
      sheetEditorEntity.selectedNote &&
      sheetEditorEntity.notesCustomAttributesEditor.currNoteCustomAttributes
    ) {
      sheetEditorEntity.selectedNote.hand = handValue;
      sheetEditorEntity.notesCustomAttributesEditor.currNoteCustomAttributes.hand =
        handValue;
    }
  };

  const handleXOffsetChange = (value: number) => {
    sheetEditorEntity.notesCustomAttributesEditor.setXOffset(value);
  };

  const { t } = useTranslation();

  const accidentalOptions = [
    { value: null, label: t('sheetEditor.accidental_none'), icon: null }, // None option
    { value: 'natural', label: '', icon: Icons.Natural },
    { value: 'sharp', label: '', icon: Icons.Sharp },
    { value: 'flat', label: '', icon: Icons.Flat },
  ];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='flex flex-col rounded-md bg-popover cursor-grab m-4 border shadow-popover select-none'
    >
      <FormHeader
        title={t('sheetEditor.popup_title')}
        leftContent={
          // Only the icon will activate dragging:
          <div {...listeners} {...attributes} className='drag-handle'>
            <PiDotsSixVerticalBold
              className={`${transform ? 'fill-blue-500' : 'fill-black'}`}
              size={24}
            />
          </div>
        }
        rightContent={
          <>
            <IconButton data-testid='cancel_changes_btn'>
              <PiXLight
                size={24}
                onClick={(e) => {
                  e.stopPropagation();
                  cancelChanges();
                  console.log('!!!Cancel changes');
                }}
              />
            </IconButton>
            <IconButton
              type='submit'
              onClick={applyChanges}
              data-testid='apply_changes_btn'
            >
              <PiCheckLight size={24} />
            </IconButton>
          </>
        }
        tooltipText={t('sheetEditor.tooltip_text')}
      />
      <Separator />
      <div className='flex text-sm flex-col gap-4 p-3'>
        <div className='grid grid-cols-2 gap-4'>
          <div className='grid w-full max-w-sm items-center gap-1.5'>
            <Label>{t('sheetEditor.finger_number')}</Label>
            <Select
              value={sheetEditorEntity.xmlEditor.fingNumber?.toString()} // Convert to string for Select compatibility
              onValueChange={(value) => {
                sheetEditorEntity.xmlEditor.fingNumber = parseInt(value); // Convert back to number
              }}
            >
              <SelectTrigger>
                <SelectValue>
                  {sheetEditorEntity.xmlEditor.fingNumber ??
                    t('sheetEditor.select')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='1'>1</SelectItem>
                <SelectItem value='2'>2</SelectItem>
                <SelectItem value='3'>3</SelectItem>
                <SelectItem value='4'>4</SelectItem>
                <SelectItem value='5'>5</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='grid w-full max-w-sm items-center gap-1.5'>
            <Label>{t('sheetEditor.finger_position')}</Label>
            <Select
              value={sheetEditorEntity.xmlEditor.fingPos}
              onValueChange={(value) => {
                sheetEditorEntity.xmlEditor.fingPos = value as any;
              }}
            >
              <SelectTrigger>
                <SelectValue>
                  {sheetEditorEntity.xmlEditor.fingPos === 'below'
                    ? t('sheetEditor.position_below')
                    : sheetEditorEntity.xmlEditor.fingPos === 'above'
                    ? t('sheetEditor.position_above')
                    : t('sheetEditor.select')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='below'>
                  {t('sheetEditor.position_below')}
                </SelectItem>
                <SelectItem value='above'>
                  {t('sheetEditor.position_above')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className='grid w-full max-w-sm items-center gap-1.5'>
          <Label>{t('sheetEditor.accidental')}</Label>
          <div className='flex justify-between gap-4 border rounded-md px-2 py-1.5'>
            {accidentalOptions.map(({ value, label, icon: Icon }, index) => (
              <label
                key={index}
                className='flex items-center gap-2 cursor-pointer'
              >
                <input
                  type='radio'
                  value={value ?? ''}
                  checked={sheetEditorEntity.xmlEditor.accidentalName === value}
                  onChange={(e: any) => {
                    if (e.target.checked) {
                      sheetEditorEntity.xmlEditor.accidentalName = value;
                    }
                  }}
                  className='accent-black'
                />
                {Icon ? <Icon /> : <span className='text-sm'>{label}</span>}
              </label>
            ))}
          </div>
        </div>
        {/*
          <div>
            Горизонтальное смещение
            <input
              type="number"
              value={fingX}
              onChange={(e) => setFingX(parseInt(e.target.value))}
            />
          </div>
          <div>
            Вертикальное смещение
            <input
              type="number"
              value={fingY}
              onChange={(e) => setFingY(parseInt(e.target.value))}
            />
          </div>
          */}
        <div className='grid w-full max-w-sm items-center gap-1.5'>
          <Label>{t('sheetEditor.move_to_other_hand')}</Label>
          <div className='rounded-md flex items-center flex-none'>
            <IconButton
              onClick={() => {
                setHand(1); // Set to left hand
                if (
                  sheetEditorEntity.selectedNote &&
                  sheetEditorEntity.notesCustomAttributesEditor
                    .currNoteCustomAttributes
                ) {
                  sheetEditorEntity.selectedNote.hand = 1;
                  sheetEditorEntity.notesCustomAttributesEditor.currNoteCustomAttributes.hand = 1;
                }
              }}
              iconName='HandLeft'
              data-testid='sheet_editor_hand_change_left_btn'
              noPadding
              noHover
              classNameIcon={`w-[36px] h-[36px] fill-black hover:fill-accent-blue ${
                hand === 1 && 'fill-accent-blue'
              }`}
            />
            <IconButton
              onClick={() => {
                setHand(0); // Set to right hand
                if (
                  sheetEditorEntity.selectedNote &&
                  sheetEditorEntity.notesCustomAttributesEditor
                    .currNoteCustomAttributes
                ) {
                  sheetEditorEntity.selectedNote.hand = 0;
                  sheetEditorEntity.notesCustomAttributesEditor.currNoteCustomAttributes.hand = 0;
                }
              }}
              iconName='HandRight'
              data-testid='sheet_editor_hand_change_right_btn'
              noPadding
              noHover
              classNameIcon={`w-[36px] h-[36px] fill-black hover:fill-accent-blue ${
                hand === 0 && 'fill-accent-blue'
              }`}
            />
          </div>
        </div>

        <div className='grid w-full max-w-sm items-center gap-1.5'>
          <Label htmlFor='note-x-offset'>
            {t('sheetEditor.move_note_horizontally')}
          </Label>
          <NumberInput
            data-testid='note-x-offset-input'
            value={Math.round(
              (sheetEditorEntity.notesCustomAttributesEditor
                ?.currNoteCustomAttributes?.xOffset ?? 0) / X_OFFSET_STEP
            )}
            onChange={(stepValue) => {
              handleXOffsetChange(stepValue * X_OFFSET_STEP);
            }}
            className='rounded-md border p-2'
            min={-100}
            max={100}
            step={1}
          />
        </div>
      </div>
    </div>
  );
});

export default SheetEditorPopup;
